import { useState, useEffect, useCallback, useRef } from 'react';
import { useIsMobile } from '../../../hooks/useResponsive';
import LocationService from '../../../services/locationService';
import { personsApi } from '../../../services/apiClient';
import enoceanService from '../../../services/enoceanService';
import { getLocationId, findLocationById } from '../utils/locationTransformers';

/**
 * Hook for managing selected location state and associated data
 * Handles loading devices, persons, and EnOcean buttons for the selected location
 */
export const useLocationSelection = ({ hierarchicalLocations }) => {
  const isMobile = useIsMobile();

  // Selection state
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);

  // Devices
  const [devices, setDevices] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(false);

  // Persons
  const [locationPersons, setLocationPersons] = useState([]);
  const [loadingPersons, setLoadingPersons] = useState(false);

  // EnOcean buttons
  const [enoceanButtons, setEnoceanButtons] = useState([]);
  const [loadingButtons, setLoadingButtons] = useState(false);
  const [buttonError, setButtonError] = useState(null);

  // Ref for the detail panel scroll
  const detailPanelRef = useRef(null);

  // AbortController ref for cancelling in-flight requests
  const abortControllerRef = useRef(null);

  // Check URL parameters for selected location
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const selectedId = urlParams.get('selectedId');

    if (selectedId && hierarchicalLocations.length > 0) {
      const location = findLocationById(hierarchicalLocations, selectedId);
      if (location) {
        setSelectedLocation(location);
        setSelectedTab(1); // Switch to devices tab
      }
    }
  }, [hierarchicalLocations]);

  // Auto-scroll detail panel when location changes
  useEffect(() => {
    if (selectedLocation && detailPanelRef.current && !isMobile) {
      detailPanelRef.current.scrollTo({ top: 0, behavior: 'auto' });
    }
  }, [selectedLocation, isMobile]);

  // Load EnOcean buttons when location changes
  const loadEnoceanButtons = useCallback(async (locationId, signal) => {
    if (!locationId) {
      setEnoceanButtons([]);
      return;
    }

    setLoadingButtons(true);
    setButtonError(null);

    try {
      const response = await enoceanService.getButtons({ locationId });
      if (signal?.aborted) return;
      if (response.success) {
        setEnoceanButtons(response.data || []);
      } else {
        setEnoceanButtons([]);
      }
    } catch (error) {
      if (signal?.aborted) return;
      console.error('Error loading EnOcean buttons:', error);
      setButtonError('Failed to load EnOcean buttons');
      setEnoceanButtons([]);
    } finally {
      if (!signal?.aborted) setLoadingButtons(false);
    }
  }, []);

  useEffect(() => {
    if (selectedLocation) {
      loadEnoceanButtons(getLocationId(selectedLocation));
    } else {
      setEnoceanButtons([]);
    }
  }, [selectedLocation, loadEnoceanButtons]);

  // Handle location selection - loads devices and persons
  const selectLocation = useCallback(async (location) => {
    // Cancel any in-flight requests from previous selection
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setSelectedLocation(location);
    // Reset tab to Overview when switching locations
    setSelectedTab(0);

    // Scroll detail panel to top
    if (detailPanelRef.current && !isMobile) {
      setTimeout(() => {
        if (detailPanelRef.current) {
          detailPanelRef.current.scrollTo({ top: 0, behavior: 'auto' });
        }
      }, 0);
    }

    if (location) {
      const locationId = getLocationId(location);

      // Load devices
      setLoadingDevices(true);
      try {
        const deviceResponse = await LocationService.getLocationDevices(locationId);
        if (controller.signal.aborted) return;
        setDevices(deviceResponse.data || deviceResponse || []);
      } catch (error) {
        if (controller.signal.aborted) return;
        console.error('Error loading devices:', error);
        setDevices([]);
      } finally {
        if (!controller.signal.aborted) setLoadingDevices(false);
      }

      // Load persons
      setLoadingPersons(true);
      try {
        const personsResponse = await personsApi.getByLocation(locationId);
        if (controller.signal.aborted) return;
        const personsData = personsResponse.data?.data || personsResponse.data || [];
        setLocationPersons(personsData);
      } catch (error) {
        if (controller.signal.aborted) return;
        console.error('Error loading persons:', error);
        setLocationPersons([]);
      } finally {
        if (!controller.signal.aborted) setLoadingPersons(false);
      }
    } else {
      setDevices([]);
      setLocationPersons([]);
    }
  }, [isMobile]);

  // Cleanup abort controller on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Handle delete EnOcean button
  const handleDeleteButton = useCallback(async (button) => {
    if (!window.confirm(`Are you sure you want to delete button "${button.friendlyName || button.hexCode}"?`)) {
      return;
    }

    try {
      await enoceanService.deleteButton(button.id);
      if (selectedLocation) {
        loadEnoceanButtons(getLocationId(selectedLocation));
      }
    } catch (error) {
      console.error('Error deleting button:', error);
      setButtonError('Failed to delete button');
    }
  }, [selectedLocation, loadEnoceanButtons]);

  // Refresh current selection data
  const refreshSelection = useCallback(async () => {
    if (selectedLocation) {
      await selectLocation(selectedLocation);
    }
  }, [selectedLocation, selectLocation]);

  return {
    // Selection
    selectedLocation,
    setSelectedLocation,
    selectLocation,
    selectedTab,
    setSelectedTab,

    // Devices
    devices,
    setDevices,
    loadingDevices,

    // Persons
    locationPersons,
    loadingPersons,

    // EnOcean
    enoceanButtons,
    loadingButtons,
    buttonError,
    setButtonError,
    handleDeleteButton,

    // Refs
    detailPanelRef,

    // Actions
    refreshSelection
  };
};

export default useLocationSelection;
